import React, { useState, useCallback } from 'react';
import { Tag, TagCategory, BookFilters } from '../types';
import './FilterPanel.css';

export interface TagsByCategory {
  category: TagCategory;
  tags: Tag[];
}

export interface FilterPanelProps {
  /** Tags disponíveis organizadas por categoria */
  availableTags: TagsByCategory[];
  /** Callback chamado quando os filtros mudam */
  onFilterChange: (filters: BookFilters) => void;
  /** Número de livros encontrados com os filtros atuais */
  bookCount?: number;
  /** Estado de carregamento */
  isLoading?: boolean;
  /** Callback para fechar o painel (mobile) */
  onClose?: () => void;
}

interface AccordionState {
  [categoryId: number]: boolean;
}

/**
 * Componente FilterPanel
 * 
 * Permite filtragem de livros por:
 * - Busca por nome/título
 * - Tags organizadas em categorias (acordeão com checkboxes)
 * - Disponibilidade física (toggle)
 * 
 * Funcionalidades:
 * - Campo de busca por texto
 * - Acordeão expansível para cada categoria de tags
 * - Checkboxes para seleção múltipla de tags
 * - Toggle para filtro de disponibilidade física
 * - Botão para limpar todos os filtros
 * - Contador de livros encontrados
 * 
 * Valida: Requisitos 10.1, 10.2, 10.3, 10.4, 10.5
 */
export function FilterPanel({
  availableTags,
  onFilterChange,
  bookCount,
  isLoading = false,
  onClose,
}: FilterPanelProps): React.ReactElement {
  // Estado dos filtros selecionados
  const [searchText, setSearchText] = useState('');
  const [selectedTags, setSelectedTags] = useState<number[]>([]);
  const [physicallyAvailable, setPhysicallyAvailable] = useState<boolean | undefined>(undefined);
  const [isAwarded, setIsAwarded] = useState<boolean | undefined>(undefined);
  const [boxType, setBoxType] = useState<string | undefined>(undefined);
  
  // Estado do acordeão (quais categorias estão expandidas)
  const [accordionState, setAccordionState] = useState<AccordionState>(() => {
    // Inicializa todas as categorias como expandidas
    const initialState: AccordionState = {};
    availableTags.forEach((cat) => {
      initialState[cat.category.id] = true;
    });
    return initialState;
  });

  // Emite mudança de filtros para o componente pai
  const emitFilterChange = useCallback(
    (search: string, tags: number[], available: boolean | undefined, awarded: boolean | undefined, box: string | undefined) => {
      const filters: BookFilters = {};
      
      if (search.trim()) {
        filters.search = search.trim();
      }
      
      if (tags.length > 0) {
        filters.tags = tags;
      }
      
      if (available !== undefined) {
        filters.physicallyAvailable = available;
      }

      if (awarded !== undefined) {
        filters.isAwarded = awarded;
      }

      if (box !== undefined) {
        filters.boxType = box;
      }
      
      onFilterChange(filters);
    },
    [onFilterChange]
  );

  // Toggle de categoria no acordeão
  const toggleAccordion = (categoryId: number) => {
    setAccordionState((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  // Handler para busca por texto (filtragem local instantânea)
  const handleSearchChange = (value: string) => {
    setSearchText(value);
    emitFilterChange(value, selectedTags, physicallyAvailable, isAwarded, boxType);
  };

  // Handler para seleção/deseleção de tag
  const handleTagChange = (tagId: number, checked: boolean) => {
    const newSelectedTags = checked
      ? [...selectedTags, tagId]
      : selectedTags.filter((id) => id !== tagId);
    
    setSelectedTags(newSelectedTags);
    emitFilterChange(searchText, newSelectedTags, physicallyAvailable, isAwarded, boxType);
  };

  // Handler para toggle de disponibilidade física
  const handleAvailabilityChange = (checked: boolean) => {
    const newAvailability = checked ? true : undefined;
    setPhysicallyAvailable(newAvailability);
    emitFilterChange(searchText, selectedTags, newAvailability, isAwarded, boxType);
  };

  // Handler para toggle de livros premiados
  const handleAwardedChange = (checked: boolean) => {
    const newAwarded = checked ? true : undefined;
    setIsAwarded(newAwarded);
    emitFilterChange(searchText, selectedTags, physicallyAvailable, newAwarded, boxType);
  };

  // Handler para filtro de tipo de caixa
  const handleBoxTypeChange = (value: string) => {
    const newBoxType = value || undefined;
    setBoxType(newBoxType);
    emitFilterChange(searchText, selectedTags, physicallyAvailable, isAwarded, newBoxType);
  };

  // Handler para limpar todos os filtros
  const handleClearFilters = () => {
    setSearchText('');
    setSelectedTags([]);
    setPhysicallyAvailable(undefined);
    setIsAwarded(undefined);
    setBoxType(undefined);
    emitFilterChange('', [], undefined, undefined, undefined);
  };

  // Verifica se há filtros ativos
  const hasActiveFilters = searchText.trim() !== '' || selectedTags.length > 0 || physicallyAvailable !== undefined || isAwarded !== undefined || boxType !== undefined;

  // Conta total de tags selecionadas
  const totalSelectedTags = selectedTags.length;

  return (
    <div className="filter-panel">
      <div className="filter-panel__header">
        <h3 className="filter-panel__title">Filtros</h3>
        {hasActiveFilters && (
          <button
            type="button"
            className="filter-panel__clear-button"
            onClick={handleClearFilters}
            disabled={isLoading}
          >
            Limpar filtros
          </button>
        )}
      </div>

      {/* Contador de livros encontrados */}
      {bookCount !== undefined && (
        <div className="filter-panel__book-count">
          {isLoading ? (
            <span className="filter-panel__book-count-loading">Buscando...</span>
          ) : (
            <span>
              <strong>{bookCount}</strong> {bookCount === 1 ? 'projeto encontrado' : 'projetos encontrados'}
            </span>
          )}
        </div>
      )}

      {/* Resumo de filtros ativos */}
      {hasActiveFilters && (
        <div className="filter-panel__active-filters">
          {searchText.trim() && (
            <span className="filter-panel__active-filter-badge filter-panel__active-filter-badge--search">
              Busca: "{searchText}"
            </span>
          )}
          {totalSelectedTags > 0 && (
            <span className="filter-panel__active-filter-badge">
              {totalSelectedTags} {totalSelectedTags === 1 ? 'tag selecionada' : 'tags selecionadas'}
            </span>
          )}
          {physicallyAvailable && (
            <span className="filter-panel__active-filter-badge filter-panel__active-filter-badge--availability">
              Disponível fisicamente
            </span>
          )}
          {isAwarded && (
            <span className="filter-panel__active-filter-badge filter-panel__active-filter-badge--awarded">
              Premiado
            </span>
          )}
        </div>
      )}

      {/* Campo de busca por nome */}
      <div className="filter-panel__search-section">
        <label className="filter-panel__search-label" htmlFor="search-input">
          Buscar por nome
        </label>
        <div className="filter-panel__search-wrapper">
          <span className="filter-panel__search-icon">🔍</span>
          <input
            id="search-input"
            type="text"
            className="filter-panel__search-input"
            placeholder="Digite o título do projeto..."
            value={searchText}
            onChange={(e) => handleSearchChange(e.target.value)}
            disabled={isLoading}
          />
          {searchText && (
            <button
              type="button"
              className="filter-panel__search-clear"
              onClick={() => handleSearchChange('')}
              aria-label="Limpar busca"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Toggle de disponibilidade física */}
      <div className="filter-panel__availability-section">
        <label className="filter-panel__toggle-label">
          <span className="filter-panel__toggle-text">Disponível fisicamente</span>
          <div className="filter-panel__toggle-wrapper">
            <input
              type="checkbox"
              className="filter-panel__toggle-input"
              checked={physicallyAvailable === true}
              onChange={(e) => handleAvailabilityChange(e.target.checked)}
              disabled={isLoading}
            />
            <span className="filter-panel__toggle-slider" />
          </div>
        </label>
      </div>

      {/* Toggle de livros premiados */}
      <div className="filter-panel__availability-section">
        <label className="filter-panel__toggle-label">
          <span className="filter-panel__toggle-text">🏆 Projetos premiados</span>
          <div className="filter-panel__toggle-wrapper">
            <input
              type="checkbox"
              className="filter-panel__toggle-input"
              checked={isAwarded === true}
              onChange={(e) => handleAwardedChange(e.target.checked)}
              disabled={isLoading}
            />
            <span className="filter-panel__toggle-slider" />
          </div>
        </label>
      </div>

      {/* Filtro de tipo de caixa */}
      <div className="filter-panel__availability-section">
        <label className="filter-panel__search-label" htmlFor="box-type-filter">
          📦 Tipo de Caixa
        </label>
        <select
          id="box-type-filter"
          className="filter-panel__search-input"
          value={boxType || ''}
          onChange={(e) => handleBoxTypeChange(e.target.value)}
          disabled={isLoading}
          style={{ cursor: 'pointer' }}
        >
          <option value="">Todos os tipos</option>
          <option value="rigida">Caixa Rígida</option>
          <option value="semirrigida">Caixa Semirrígida</option>
          <option value="hibrida">Caixa Híbrida (Rígida e Semirrígida)</option>
        </select>
      </div>

      {/* Acordeão de categorias de tags */}
      <div className="filter-panel__categories">
        {availableTags.map((categoryData) => {
          const isExpanded = accordionState[categoryData.category.id] ?? true;
          const selectedInCategory = categoryData.tags.filter((tag) =>
            selectedTags.includes(tag.id)
          ).length;

          return (
            <div
              key={categoryData.category.id}
              className={`filter-panel__category ${isExpanded ? 'filter-panel__category--expanded' : ''}`}
            >
              <button
                type="button"
                className="filter-panel__category-header"
                onClick={() => toggleAccordion(categoryData.category.id)}
                aria-expanded={isExpanded}
                aria-controls={`category-${categoryData.category.id}`}
              >
                <span className="filter-panel__category-title">
                  {categoryData.category.displayName}
                  {selectedInCategory > 0 && (
                    <span className="filter-panel__category-selected-count">
                      ({selectedInCategory})
                    </span>
                  )}
                </span>
                <span className={`filter-panel__category-icon ${isExpanded ? 'filter-panel__category-icon--expanded' : ''}`}>
                  ▼
                </span>
              </button>

              <div
                id={`category-${categoryData.category.id}`}
                className={`filter-panel__category-content ${isExpanded ? 'filter-panel__category-content--visible' : ''}`}
              >
                {categoryData.tags.length === 0 ? (
                  <p className="filter-panel__empty-category">
                    Nenhuma tag nesta categoria
                  </p>
                ) : (
                  <div className="filter-panel__tag-list">
                    {categoryData.tags.map((tag) => (
                      <label
                        key={tag.id}
                        className={`filter-panel__tag-item ${selectedTags.includes(tag.id) ? 'filter-panel__tag-item--selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          className="filter-panel__tag-checkbox"
                          checked={selectedTags.includes(tag.id)}
                          onChange={(e) => handleTagChange(tag.id, e.target.checked)}
                          disabled={isLoading}
                        />
                        <span className="filter-panel__tag-checkmark" />
                        <span className="filter-panel__tag-name">{tag.name}</span>
                        {tag.bookCount !== undefined && (
                          <span className="filter-panel__tag-count">({tag.bookCount})</span>
                        )}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Mensagem quando não há tags disponíveis */}
      {availableTags.length === 0 && (
        <div className="filter-panel__no-tags">
          <p>Nenhuma tag disponível para filtrar.</p>
        </div>
      )}

      {/* Botão Filtrar (mobile) */}
      {onClose && (
        <div className="filter-panel__mobile-actions">
          <button
            type="button"
            className="filter-panel__apply-button"
            onClick={onClose}
          >
            🔍 Filtrar
          </button>
        </div>
      )}
    </div>
  );
}

export default FilterPanel;
